import { lazy, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, RouteObject } from 'react-router';

// api's
import { CompanyDetail, getCompanyDetail } from 'utils/raw-api';

// components
import Loadable from 'components/Loadable';

// hooks
import useDynamicConfig from 'hooks/useDynamicConfig';
import { useGetCompanyQuery } from 'store/reducers/api/company';
import useUserRole from 'hooks/useUserRole';
import useRoleChecks from 'hooks/useRoleChecks';
import useSkipCompanyQuery from 'hooks/useSkipCompanyQuery';

// pages
const CompanyLevel = Loadable(lazy(() => import('pages/companies/company-level')));
const CompanyOverview = Loadable(lazy(() => import('pages/companies/company-overview')));
const Dashboard = Loadable(lazy(() => import('pages/companies/admin')));
const CompanyProperties = Loadable(lazy(() => import('pages/companies/company-properties')));
const CompanyAlerts = Loadable(lazy(() => import('pages/companies/company-alerts')));
const CompanyMicroMeters = Loadable(lazy(() => import('pages/companies/company-micro-meters')));
const CompanySubmissions = Loadable(lazy(() => import('pages/companies/company-submissions')));
const CompanyThresholds = Loadable(lazy(() => import('pages/companies/company-thresholds')));
//const CompanyUsers = Loadable(lazy(() => import('pages/companies/company-users')));
const CompanyCharts = Loadable(lazy(() => import('pages/companies/company-charts')));
const CompanyInvoices = Loadable(lazy(() => import('pages/companies/company-invoices')));
const CompanySavings = Loadable(lazy(() => import('pages/companies/company-savings')));
const CompanyCityBills = Loadable(lazy(() => import('pages/companies/company-city-bills')));

const useCompanyLevelRoutes = () => {
  const { activeRole } = useUserRole();
  const { SYSTEM_ROLES, COMPANY_ROLES } = useDynamicConfig();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const { data: userCompany, isLoading: loading } = useGetCompanyQuery(activeRole?.company_id!, {
    skip: skipCompanyQuery
  });
  const { isAboveSystemSupport } = useRoleChecks();

  return useMemo(() => {
    const routes: RouteObject = {
      path: 'companies',
      handle: {
        crumb: () => (isAboveSystemSupport ? null : <FormattedMessage id="companies" />),
        allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES]
      },
      children: [
        {
          path: ':id',
          element: <CompanyLevel />,
          loader: async ({ params }) => {
            if (params.id) {
              return await getCompanyDetail(Number(params.id));
            }
          },
          handle: {
            crumb: ({ company }: CompanyDetail) => <>{company?.name}</>,
            title: ({ company }: CompanyDetail) => <>{company?.name}</>,
            subtitle: ({ company, city, state, country }: CompanyDetail) => {
              const address = [company?.street, city?.name, state?.name, country?.name].filter((x) => x).join(', ');
              return <>{address}</>;
            }
          },
          children: [
            {
              path: '',
              element: <Navigate to="properties" replace />
            },
            {
              path: 'dashboard',
              element: <Dashboard />,
              handle: {
                crumb: () => <FormattedMessage id="dashboard" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'overview',
              element: <CompanyOverview />,
              handle: {
                crumb: () => <FormattedMessage id="overview" />
              }
            },
            {
              path: 'properties',
              element: <CompanyProperties />,
              handle: {
                crumb: () => <FormattedMessage id="properties" />
              }
            },
            {
              path: 'alerts',
              element: <CompanyAlerts />,
              handle: {
                crumb: () => <FormattedMessage id="alerts" />
              }
            },
            {
              path: 'micrometers',
              element: <CompanyMicroMeters />,
              handle: {
                crumb: () => <FormattedMessage id="micrometers" />
              }
            },
            {
              path: 'submissions',
              element: <CompanySubmissions />,
              handle: {
                crumb: () => <FormattedMessage id="submissions" />
              }
            },
            {
              path: 'thresholds',
              element: <CompanyThresholds />,
              handle: {
                crumb: () => <FormattedMessage id="thresholds" />
              }
            },
            /*{
            path: 'users',
            element: <CompanyUsers />,
            handle: {
              crumb: () => <FormattedMessage id="users" />
            }
          },*/
            {
              path: 'analytics',
              element: <CompanyCharts />,
              handle: {
                crumb: () => <FormattedMessage id="analytics" />
              }
            },
            {
              path: 'savings',
              element: <CompanySavings />,
              handle: {
                crumb: () => <FormattedMessage id="savings" />,
                allowedRoles: (userCompany?.expose_savings || 0) === 0 ? SYSTEM_ROLES : [...SYSTEM_ROLES, ...COMPANY_ROLES]
              }
            },
            {
              path: 'billing',
              handle: {
                crumb: () => <FormattedMessage id="billing" />,
                allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES]
              },
              children: [
                {
                  index: true,
                  element: <Navigate to="city-bills" replace />
                },
                {
                  path: 'city-bills',
                  element: <CompanyCityBills />,
                  handle: {
                    crumb: () => <FormattedMessage id="city-bills" />
                  }
                },
                {
                  path: 'invoices',
                  element: <CompanyInvoices />,
                  handle: {
                    crumb: () => <FormattedMessage id="invoices" />
                  }
                }
              ]
            }
          ]
        }
      ]
    };

    if (loading) return {};

    return {
      ...routes,
      children: routes.children?.map((route) => ({
        ...route,
        children: filterChildRoutes(route.children, userCompany?.company_type_id)
      })) as RouteObject[]
    };
  }, [SYSTEM_ROLES, COMPANY_ROLES, userCompany, loading, isAboveSystemSupport]);
};

export default useCompanyLevelRoutes;

const filterChildRoutes = (children?: RouteObject[], company_type_id?: number) => {
  if (company_type_id === 3) {
    return children?.filter((child) => ['', 'properties'].includes(child.path as string));
  }
  return children;
};
